import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  BadgeCheck,
  Building2,
  ChevronLeft,
  Copy,
  ExternalLink,
  IdCard,
  Mail,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  UserRound,
} from 'lucide-react';
import { api, dateOnly } from '../lib/api';
import { EmptyState, Status } from '../components/UI';
import { CredentialWorkspace } from './CompanyIDAdministration';

const req = (method, body = {}) => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

const titleCase = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase());
const initials = value => String(value || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '?';

export default function CompanyIDRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [revealedCode, setRevealedCode] = useState('');
  const [presentation, setPresentation] = useState(null);
  const [qr, setQr] = useState('');

  const row = data?.credential || null;

  const load = useCallback(async (quiet = false) => {
    if (!id) return;
    if (!quiet) setLoading(true);
    try {
      const result = await api(`/api/admin/company-id/${encodeURIComponent(id)}`);
      setData(result);
      if (!quiet) setNotice('');
    } catch (error) {
      setNotice(`Error: ${error.message}`);
      if (!quiet) setData(null);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (!row?.id || row.status !== 'active') {
      setPresentation(null);
      setQr('');
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const issuePresentation = async () => {
      try {
        const result = await api(`/api/admin/company-id/${encodeURIComponent(row.id)}/presentation`, req('POST'));
        const image = await QRCode.toDataURL(result.verification_url, {
          width: 330,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        if (!cancelled) {
          setPresentation(result);
          setQr(image);
        }
      } catch {
        if (!cancelled) {
          setPresentation(null);
          setQr('');
        }
      }
    };
    issuePresentation();
    timer = window.setInterval(issuePresentation, 60000);
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [row?.id, row?.status]);

  const copy = async value => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice('Copied to clipboard.');
    } catch {
      setNotice('Error: Your browser did not allow copying.');
    }
  };

  const refreshRelationship = async current => {
    setBusy(true);
    setNotice('');
    try {
      await api(`/api/admin/company-id/${encodeURIComponent(current.id)}/refresh-companies-house`, req('POST'));
      setNotice('Company relationship refreshed.');
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reveal = async current => {
    if (revealedCode) {
      setRevealedCode('');
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      const result = await api(`/api/admin/company-id/${encodeURIComponent(current.id)}/reveal-personal-code`, req('POST'));
      setRevealedCode(result.personal_code || '');
      window.setTimeout(() => setRevealedCode(''), 60000);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const rotatePublicLink = async current => {
    if (!window.confirm('Rotate this Company ID permanent verification link? Existing permanent links will stop working immediately.')) return;
    setBusy(true);
    setNotice('');
    try {
      await api(`/api/admin/company-id/${encodeURIComponent(current.id)}/rotate-public-link`, req('POST'));
      setNotice('Permanent verification link rotated.');
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (current, status) => {
    let reason = '';
    if (status === 'revoked') {
      reason = window.prompt('Reason for revoking this Company ID:') || '';
      if (!reason.trim()) return;
    }
    if (status === 'suspended' && !window.confirm('Suspend this Company ID? Live verification will immediately stop reporting it as valid.')) return;
    setBusy(true);
    setNotice('');
    try {
      await api(`/api/admin/company-id/${encodeURIComponent(current.id)}/status`, req('POST', { status, reason }));
      setNotice(`Company ID ${status}.`);
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const issueAppleWallet = async (current, deliveryEmail = '') => {
    setBusy(true);
    setNotice('');
    try {
      const result = await api(`/api/admin/company-id/${encodeURIComponent(current.id)}/wallet/apple`, req('POST', {
        delivery_email: String(deliveryEmail || '').trim(),
      }));
      const delivery = result?.email_sent
        ? ` Wallet email sent to ${result.delivery_email}.`
        : result?.delivery_email
          ? ' Pass Studio did not report an email delivery.'
          : ' No staff email address is available, so use the Apple Wallet link directly.';
      const fieldNote = Array.isArray(result?.mapped_fields) && result.mapped_fields.length
        ? ` Synced fields: ${result.mapped_fields.join(', ')}.`
        : '';
      const baseMessage = result?.resent_existing
        ? 'Existing Apple Wallet pass updated and delivery email requested again.'
        : result?.already_issued
          ? 'Apple Wallet pass already exists and is ready to use.'
          : 'Apple Wallet pass issued through Pass Studio.';
      setNotice(baseMessage + fieldNote + delivery);
      await load(true);
    } catch (error) {
      setNotice(`Error: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <section className="cid-platform cid-record-platform">
    <div className="cid-loading"><div className="cid-spinner"/><strong>Loading credential record</strong><span>Reading the issued credential, verifier, Wallet state and audit trail…</span></div>
  </section>;

  if (!row) return <section className="cid-platform cid-record-platform">
    <button className="cid-record-back" onClick={() => navigate('/company-id?tab=credentials')}><ChevronLeft size={16}/>Credential Registry</button>
    {notice && <div className="cid-notice error">{notice}</div>}
    <EmptyState title="Company ID record unavailable" body="This credential could not be loaded or you do not have access to it."/>
  </section>;

  const role = row.internal_title || titleCase(row.companies_house_role) || 'Staff member';
  const relationshipCurrent = ['verified', 'employer_verified'].includes(row.relationship_status);

  return <section className="cid-platform cid-record-platform">
    <div className="cid-record-toolbar">
      <button className="cid-record-back" onClick={() => navigate('/company-id?tab=credentials')}><ChevronLeft size={16}/>Credential Registry</button>
      <div className="cid-record-toolbar-actions">
        {row.staff_id && <button className="cid-button cid-button-ghost cid-button-small" onClick={() => navigate(`/company-id/holder/${encodeURIComponent(row.staff_id)}`)}><UserRound size={14}/>View holder identity</button>}
        <button className="cid-button cid-button-ghost cid-button-small" onClick={() => load(false)} disabled={busy}><RefreshCw size={14}/>Refresh</button>
        {row.persistent_verification_url && <a className="cid-button cid-button-ghost cid-button-small" href={row.persistent_verification_url} target="_blank" rel="noreferrer"><ExternalLink size={14}/>Open verifier</a>}
      </div>
    </div>

    {notice && <div className={`cid-notice ${notice.startsWith('Error:') ? 'error' : ''}`}>{notice}</div>}

    <section className="cid-record-profile">
      <div className="cid-record-avatar">
        {row.photo_url ? <img src={row.photo_url} alt=""/> : <span>{initials(row.legal_name)}</span>}
      </div>
      <div className="cid-record-identity">
        <span className="cid-record-kicker">ISSUED CREDENTIAL RECORD</span>
        <h1 className="govuk-heading-xl">{row.legal_name}</h1>
        <p className="govuk-body">{role}</p>
        <div className="cid-record-contact">
          <span><Building2 size={14}/>{row.company_name}</span>
          <span><Mail size={14}/>{row.staff_email || 'No email on record'}</span>
        </div>
      </div>
      <div className="cid-record-status-block">
        <Status value={row.status}/>
        <span className={`cid-record-wallet-state ${row.wallet?.issued ? 'ready' : ''}`}><Smartphone size={14}/>{row.wallet?.issued ? 'Apple Wallet issued' : 'Wallet not issued'}</span>
      </div>
    </section>

    <div className="cid-record-summary-grid">
      <RecordSummary icon={IdCard} label="Holder Staff ID" value={row.staff_id_number || 'Pending'} mono/>
      <RecordSummary icon={BadgeCheck} label="Credential" value={row.credential_number} mono/>
      <RecordSummary icon={ShieldCheck} label="Relationship" value={relationshipCurrent ? 'Current' : titleCase(row.relationship_status)} positive={relationshipCurrent}/>
      <RecordSummary icon={Smartphone} label="Apple Wallet" value={row.wallet?.issued ? titleCase(row.wallet.status || 'Issued') : 'Not issued'} positive={Boolean(row.wallet?.issued)}/>
    </div>

    <section className="cid-record-profile-details">
      <div><span>Credential type</span><strong>{row.credential_label || 'Staff Identification'}</strong></div>
      <div><span>Issued</span><strong>{dateOnly(row.issued_at)}</strong></div>
      <div><span>Valid until</span><strong>{row.expires_at ? dateOnly(row.expires_at) : 'Until revoked'}</strong></div>
      <div><span>Issuer</span><strong>{row.company_name}</strong></div>
    </section>

    <CredentialWorkspace
      row={row}
      detail={data}
      qr={qr}
      presentation={presentation}
      revealedCode={revealedCode}
      busy={busy}
      reveal={reveal}
      copy={copy}
      rotatePublicLink={rotatePublicLink}
      refreshRelationship={refreshRelationship}
      changeStatus={changeStatus}
      wallet={data?.wallet}
      issueAppleWallet={issueAppleWallet}
    />
  </section>;
}

function RecordSummary({ icon: Icon, label, value, mono, positive }) {
  return <div className="cid-record-summary-card">
    <span className="cid-record-summary-icon"><Icon size={18}/></span>
    <div><span>{label}</span><strong className={`${mono ? 'mono' : ''} ${positive ? 'positive' : ''}`}>{value || '—'}</strong></div>
  </div>;
}
